import {
  FieldError,
  useController,
  Control,
  UseFormRegisterReturn,
  Controller,
} from 'react-hook-form';
import { cn } from '@/lib/utils';
import { FaCaretDown, FaCaretUp, FaCheck } from 'react-icons/fa';
import ClipLoader from 'react-spinners/ClipLoader';
import { useEffect, useRef } from 'react';
import { Slider } from '@/components/ui/slider';
import { ChangeEvent } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type FormInputProps = {
  label: string;
  id: string;
  error: FieldError | undefined;
  unit?: string;
  className?: string;
  helperText?: string;
  stepper?: boolean;
  type?: string;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  step?: string;
} & (
  | { register: UseFormRegisterReturn<string> }
  | { value: number; onChange: (e: ChangeEvent<HTMLInputElement>) => void }
);

export function FormInput({
  id,
  label,
  error,
  unit,
  className,
  stepper,
  helperText,
  ...inputProps
}: FormInputProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Get the input props based on whether we're using register or value/onChange
  const fieldProps =
    'register' in inputProps
      ? inputProps.register
      : {
          value: inputProps.value,
          onChange: inputProps.onChange,
        };

  useEffect(() => {
    const inputEl = inputRef.current;
    if (inputEl && inputProps.type === 'number') {
      const preventScroll = (e: WheelEvent) => e.preventDefault();
      inputEl.addEventListener('wheel', preventScroll);

      return () => {
        inputEl.removeEventListener('wheel', preventScroll);
      };
    }
  }, [inputProps.type]);

  const showStepper = inputProps.type === 'number' && stepper;

  return (
    <div className="">
      <label
        htmlFor={id}
        className="my-1 block text-sm font-normal leading-6 text-gray-900"
      >
        {label} {inputProps.required && <span className="text-red-500">*</span>}
      </label>
      <div className={cn('relative mt-1 rounded-md shadow-sm', className)}>
        <input
          id={id}
          className={cn(
            `block w-full rounded-md border-0 bg-white px-2 py-2  text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6 `,
            error ? 'ring-red-500' : '',
            inputProps.disabled ? 'bg-gray-100' : '',
          )}
          style={{
            MozAppearance: 'textfield',
            WebkitAppearance: 'none',
          }}
          {...inputProps}
          {...fieldProps}
        />
        {unit && (
          <div
            className={cn(
              'pointer-events-none absolute inset-y-0 right-0 flex items-center ',
              showStepper ? 'pr-16' : 'pr-8',
            )}
          >
            <span className="text-gray-500 sm:text-sm">{unit}</span>
          </div>
        )}
        {/* Stepper buttons */}
        {showStepper && <Stepper id={id} />}
      </div>
      {helperText && (
        <p className="ml-1 mt-1 text-sm text-gray-500">{helperText}</p>
      )}
      {error && <p className="mt-2 text-sm text-red-600">{error.message}</p>}
    </div>
  );
}

type StepperProps = {
  id: string;
};

export function Stepper({ id }: StepperProps) {
  const handleStepUp = () => {
    const input = document.getElementById(id) as HTMLInputElement;
    if (input) input.stepUp();
  };

  const handleStepDown = () => {
    const input = document.getElementById(id) as HTMLInputElement;
    if (input) input.stepDown();
  };

  return (
    <div className="absolute inset-y-0 right-0 flex items-center space-x-1 pr-2">
      <button
        type="button"
        onClick={handleStepUp}
        className="my-auto rounded bg-gray-200 px-1 py-1 text-xs text-gray-600 hover:bg-gray-300"
      >
        <FaCaretUp className="h-3 w-3" />
      </button>
      <button
        type="button"
        onClick={handleStepDown}
        className="my-auto rounded bg-gray-200 px-1 py-1 text-xs text-gray-600 hover:bg-gray-300"
      >
        <FaCaretDown className="h-3 w-3 -translate-y-[1px]" />
      </button>
    </div>
  );
}

type SubmitButtonProps = {
  'label': string;
  'className'?: string;
  'isLoading'?: boolean;
  'disabled'?: boolean;
  'complete'?: boolean;
  'completeText'?: string;
  'onClick'?: () => void;
  'destructive'?: boolean;
  'data-testid'?: string;
};

export function SubmitButton({
  label,
  className,
  isLoading = false, // Default isLoading to false
  disabled,
  complete,
  completeText,
  onClick,
  destructive = false,
  'data-testid': dataTestId,
}: SubmitButtonProps) {
  return (
    <Button
      type="submit"
      variant={destructive ? 'destructive' : 'default'}
      disabled={isLoading || disabled} // Disable button when loading
      onClick={onClick}
      className={cn(
        'flex flex-shrink-0 items-center ',
        isLoading || disabled ? 'opacity-50' : '',
        complete &&
          'cursor-default bg-green-50 text-green-500 ring-1 ring-green-500 hover:bg-green-50',
        className,
      )}
      data-testid={dataTestId}
    >
      {isLoading && (
        <ClipLoader
          size={15}
          color="#94a3b8"
          className="mr-2 h-3 w-3 text-white"
        />
      )}
      {complete && <FaCheck className="mr-1.5 h-3 w-3 text-green-500" />}{' '}
      {complete ? completeText : label}
    </Button>
  );
}

type SecondaryFormButtonProps = SubmitButtonProps & {
  'textButton'?: boolean;
  'data-testid'?: string;
};
export function SecondaryFormButton({
  label,
  className,
  textButton,
  onClick,
  disabled,
  'data-testid': dataTestId,
}: SecondaryFormButtonProps) {
  return (
    <Button
      type="button"
      variant={textButton ? 'link' : 'outline'}
      disabled={disabled}
      onClick={onClick}
      className={cn('flex flex-shrink-0 items-center', className)}
      data-testid={dataTestId}
    >
      {label}
    </Button>
  );
}

type FormHeaderProps = {
  title: string;
  className?: string;
  tooltipContent?: string | React.ReactNode;
};

export function FormHeader({
  title,
  className,
  tooltipContent,
}: FormHeaderProps) {
  return (
    <div className={cn('mt-5', className)}>
      <h4 className="whitespace-nowrap text-lg font-medium text-gray-900">
        {title}
      </h4>
    </div>
  );
}

export function FormDivider({ className }: { className?: string }) {
  return <hr className={cn('my-4 border-gray-200', className)} />;
}

export function PrimarySecondaryButtonContainer({
  className,
  children,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'mt-4 flex items-center gap-2 py-2 md:flex-row-reverse',
        className,
      )}
    >
      {children}
    </div>
  );
}

interface FormSliderProps {
  name: string;
  control: Control<any>;
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
}

export const FormSlider = ({
  name,
  control,
  defaultValue = 0,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  className,
  ...props
}: FormSliderProps) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue,
  });

  return (
    <div className="space-y-2">
      <Slider
        ref={field.ref}
        name={field.name}
        value={[field.value]}
        onValueChange={values => field.onChange(values[0])}
        defaultValue={[defaultValue]}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        className={cn(error ? 'border-red-500' : '', className)}
        {...props}
      />
      {error && <p className="text-sm text-red-500">{error.message}</p>}
    </div>
  );
};

type SelectOption = {
  value: string;
  label: string;
};

interface FormSelectProps {
  label: string;
  id: string;
  control: Control<any>;
  name: string;
  error?: FieldError;
  required?: boolean;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
}

export function FormSelect({
  label,
  id,
  control,
  name,
  error,
  required = false,
  options,
  placeholder = 'Select an option',
  className = '',
}: FormSelectProps) {
  return (
    <div className={`space-y-2 ${className}`}>
      <label
        htmlFor={id}
        className="block text-sm font-medium"
      >
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            onValueChange={field.onChange}
            defaultValue={field.value}
          >
            <SelectTrigger
              className="w-full"
              id={id}
            >
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map(option => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />
      {error && <p className="text-sm text-red-500">{error.message}</p>}
    </div>
  );
}
